import { useMutation } from '@apollo/client';
import { DeleteConversationsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { DeleteConversationsTRPCInput } from './useDeleteConversationsTRPC';
import { useDeleteConversationsTRPC } from './useDeleteConversationsTRPC';

/**
 * Flag-gated wrapper (RSP-6496) for the batch "delete all conversations" path.
 * Returns `{ affected_rows }` from both paths.
 *
 * The tRPC `deleteMany` procedure rejects an empty `Ids` array (`.min(1)`),
 * whereas the legacy Apollo doc tolerated it (empty `_in` → 0 rows). The
 * editor's `tinycomments_delete_all` can fire with zero conversations, so the
 * wrapper short-circuits an empty list to preserve the legacy no-op behaviour.
 */
export const useDeleteConversations = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteConversationsGraphQL, graphqlState] = useMutation(
    DeleteConversationsDocument
  );

  const trpcMutation = useDeleteConversationsTRPC();

  const deleteConversations = async (
    input: DeleteConversationsTRPCInput
  ): Promise<{ affected_rows: number }> => {
    if (input.Ids.length === 0) {
      return { affected_rows: 0 };
    }

    if (trpcEnabled) {
      return trpcMutation.deleteConversations(input);
    }

    const result = await deleteConversationsGraphQL({
      variables: { Ids: input.Ids },
    });

    return {
      affected_rows: result.data?.delete_conversation?.affected_rows ?? 0,
    };
  };

  if (trpcEnabled) {
    return {
      deleteConversations,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteConversations,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
